// src/pages/TagProductsPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, Loader2 } from 'lucide-react';
import apiClient from '../api/apiClient';
import VerticalContentCard from '../components/Category/VerticalContentCard';
import BookCard from '../components/library/BookCard';
import ContentFilters from '../components/common/ContentFilters';

const TagProductsPage = () => {
  const { tagSlug } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [tagName, setTagName] = useState('');
  const [filters, setFilters] = useState({
    sortOrder: 'newest',
    isFree: null,
  });

  // مدیریت Infinite Scroll
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 10;

  const observerRef = useRef();
  const sentinelRef = useRef(null);

  const slugToTitle = (slug) => {
    return slug.split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // دریافت نام تگ
  useEffect(() => {
    const fetchTagName = async () => {
      try {
        const response = await apiClient.get('/products/tags/');
        const tags = response.data;
        const foundTag = tags.find(t => t.slug === tagSlug);
        setTagName(foundTag ? foundTag.name : slugToTitle(tagSlug));
      } catch (err) {
        console.error('Error fetching tag name:', err);
        setTagName(slugToTitle(tagSlug));
      }
    };
    fetchTagName();
  }, [tagSlug]);

  // تابع دریافت آیتم‌ها با صفحه‌بندی
  const fetchItems = useCallback(async (pageNum = 1, append = false) => {
    if (!tagSlug) return;

    if (pageNum === 1) {
      setLoading(true);
      setError(null);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = {
        limit: PAGE_SIZE,
        offset: (pageNum - 1) * PAGE_SIZE,
      };
      if (filters.sortOrder === 'newest') params.sort_by = 'newest';
      if (filters.isFree !== null) params.is_free = filters.isFree;

      // ۱. دریافت محصولات
      const productsRes = await apiClient.get(`/products/list/tag/${tagSlug}/`, { params });
      let products = [];
      let totalProducts = 0;
      if (Array.isArray(productsRes.data)) {
        products = productsRes.data;
        totalProducts = products.length;   // آرایه بود، پس همه را گرفته
      } else if (productsRes.data?.results) {
        products = productsRes.data.results;
        totalProducts = productsRes.data.count || products.length;
      }

      // ۲. دریافت کتاب‌ها
      const booksRes = await apiClient.get(`/books/`, {
        params: {
          tags__slug: tagSlug,
          limit: PAGE_SIZE,
          offset: (pageNum - 1) * PAGE_SIZE,
        },
      });
      let books = [];
      let totalBooks = 0;
      if (Array.isArray(booksRes.data)) {
        books = booksRes.data;
        totalBooks = books.length;
      } else if (booksRes.data?.results) {
        books = booksRes.data.results;
        totalBooks = booksRes.data.count || books.length;
      }

      // ترکیب
      const newItems = [
        ...books.map(book => ({ ...book, itemType: 'book' })),
        ...products.map(product => ({ ...product, itemType: 'product' })),
      ];

      // بررسی وجود آیتم‌های بیشتر
      const hasMoreBooks = (pageNum * PAGE_SIZE) < totalBooks;
      const hasMoreProducts = (pageNum * PAGE_SIZE) < totalProducts;
      setHasMore(hasMoreBooks || hasMoreProducts);

      if (append) {
        setItems(prev => [...prev, ...newItems]);
      } else {
        setItems(newItems);
      }
    } catch (err) {
      console.error('Error fetching items:', err);
      if (!append) setError('خطا در بارگذاری آثار این تگ');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [tagSlug, filters]);

  // بارگذاری اولیه و هنگام تغییر فیلتر
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchItems(1, false);
  }, [fetchItems]);

  // Intersection Observer برای اسکرول بی‌نهایت
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchItems(nextPage, true);
        }
      },
      { threshold: 0.1 }
    );

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [hasMore, loading, loadingMore, page, fetchItems]);

  const defaultOgImage = 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/og-image.jpg';
  const pageTitle = tagName ? `آثار برچسب ${tagName} | فرزانه` : 'آثار برچسب | فرزانه';
  const pageDescription = tagName
    ? `لیست کتاب‌ها و محصولات مرتبط با برچسب «${tagName}» در فرزانه.`
    : 'مشاهده آثار بر اساس برچسب در فرزانه';

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-purple-700" />
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-500 text-center p-4">{error}</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={defaultOgImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={defaultOgImage} />
      </Helmet>

      <div className="sub-categories-page rtl container mx-auto p-0 pb-20 max-w-[460px] overflow-x-hidden bg-white">
        <div className="p-4 bg-white border-b border-gray-200">
          <nav className="text-[11px] font-light text-gray-500 mb-2 flex items-center space-x-reverse space-x-1">
            <Link to="/" className="hover:text-indigo-600">خانه</Link>
            <ChevronLeft size={14} className="mt-0.5 text-gray-400" />
            <span className="text-gray-800 font-medium">{tagName}</span>
          </nav>
          <h1 className="text-2xl font-extrabold text-gray-800 truncate">
             {tagName}
          </h1>
        </div>

        <ContentFilters filters={filters} setFilters={setFilters} />

        <div className="p-4 md:p-6">
          {items.length === 0 ? (
            <div className="text-center text-gray-500 py-10">
              <p>هیچ اثری با این تگ یافت نشد.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {items.map(item => (
                item.itemType === 'book' ? (
                  <BookCard key={`book-${item.id}`} book={item} />
                ) : (
                  <VerticalContentCard key={`product-${item.id}`} data={item} />
                )
              ))}
            </div>
          )}

          {/* Sentinel برای Infinite Scroll */}
          <div ref={sentinelRef} className="h-10" />

          {/* لودینگ بیشتر */}
          {loadingMore && (
            <div className="flex justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TagProductsPage;